import React from 'react'
import { CardMembership, ContentCopy } from '@mui/icons-material';
import { Box, Stack, Typography } from '@mui/material';
import { observer, useLocalObservable } from 'mobx-react-lite'
import { ArchiveCreateModeDialog } from '../../../component/archive-form';
import { validateAnagraficaOcrFile } from '../../../component/archive-form/anagrafica-ocr';
import apiManage from '../../../request';
import GlobalNotification from '../../../component/common/snackbar';
import './scss/restaurant-manager.style.scss';
import TableComponent, { HeadCell, TableRowFeatures } from '../../../component/table/table.component';
import { RestaurantManageState } from './restaurant-managet-state';
import i18n from '../../../i18n';
import DeleteDialog from '../../../component/dialog/delete-dialog.component';
import ButtonComponent from '../../../component/button/button.component';
import RestaurantDialog from './restaurant-dialog';
import { useTranslation } from 'react-i18next';
import RestaurantAuthDialog from './restaurant-auth-dialog';
import { Restaurant } from '../../../models';
import _ from 'lodash';
import { ConditionType, ConditionsBox, SearchKeyInput, SearchKeyInputItems } from '../../../component/form/search-key-input.component';
import { toJS } from 'mobx';
import copy from 'copy-to-clipboard';

/**
 * 创建餐厅列表数据列，供页面与列表渲染测试复用。
 * @param getSortStatus 获取指定字段当前排序状态
 * @returns 不包含行操作列的餐厅列表列配置
 */
export function createRestaurantHeadCellList(
    getSortStatus: (key: string) => number,
): HeadCell[] {
    return [
        {
            id: 'nickname',
            label: i18n.t('restaurant_management_page_table_text_restaurant_name'),
            sortStatus: getSortStatus('nickname'),
        },
        {
            id: 'string_id',
            label: i18n.t('restaurant_page_input_text_restaurant_identification_code'),
            renderFunction(row: Restaurant) {
                return (
                    <div className='FC'>
                        <ButtonComponent
                            variant='text'
                            handleClick={() => {
                                // 复制结果用于决定是否展示成功提示
                                const copied = copy(row?.string_id || '');
                                if (copied) {
                                    GlobalNotification.success(
                                        i18n.t('restaurant_page_input_tip_message_copy_successfully'),
                                    );
                                }
                            }}
                        >
                            <ContentCopy />
                        </ButtonComponent>
                        <Typography variant='body2'>{row?.string_id || ''}</Typography>
                    </div>
                );
            },
        },
        {
            id: 'city',
            label: i18n.t('restaurant_management_page_table_text_city'),
            sortStatus: getSortStatus('city'),
        },
        {
            id: 'tel',
            label: i18n.t('restaurant_management_page_table_text_phone'),
            renderFunction(row: Restaurant) {
                return `${row.tel_prefix || ''} ${row.tel || ''}`;
            },
        },
        {
            id: 'reg_vat_number',
            label: i18n.t('restaurant_info_page_text_vat_number'),
            renderFunction: (row: Restaurant) => row.reg_vat_number ? (
                <Typography variant='body2'>{row.reg_vat_number}</Typography>
            ) : (
                <Stack direction='row' alignItems='center' spacing={1}>
                    <Box
                        className='restaurant-vat-missing-dot'
                        sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: 'var(--func-error)',
                            flexShrink: 0,
                        }}
                    />
                    <Typography variant='body2' sx={{ color: 'var(--func-error)' }}>
                        {i18n.t('archive_form_missing_vat', {
                            defaultValue: '缺税号',
                        })}
                    </Typography>
                </Stack>
            ),
        },
        { id: 'authorization_type', label: i18n.t('restaurant_info_page_text_way_of_authorization') },
        {
            id: 'business_labels',
            label: i18n.t('restaurant_info_page_text_service_instance_tag'),
            renderFunction: (row: Restaurant) => <>{(row?.business_labels || [])?.join('，')}</>,
        },
        {
            id: 'discount_info',
            label: i18n.t('restaurant_info_page_text_discount_information_discount_module'),
            renderFunction: (row: Restaurant) => <>{row?.discount_info ? `${row?.discount_info}%` : ''}</>,
        },
        {
            id: 'discount_annual',
            label: i18n.t('restaurant_info_page_text_discount_information_discount_annual'),
            renderFunction: (row: Restaurant) => (
                <>{row?.discount_annual || row?.discount_info ? `${row?.discount_annual || row?.discount_info}%` : ''}</>
            ),
        },
    ];
}

/**
 * 餐厅新增入口：先选择建档方式，再进入既有手动新增弹窗。
 * @param RestaurantManageData 餐厅管理状态
 */
export const RestaurantCreateEntry = observer(({
    RestaurantManageData,
    groupName,
}: {
    /** 餐厅管理状态。 */
    RestaurantManageData: RestaurantManageState;
    /** 当前餐厅所属集团名称。 */
    groupName: string;
}) => {
    return (
        <>
            <ButtonComponent
                variant='contained'
                size='small'
                extraButtonStyle='add-button'
                titleText={i18n.t('general_text_new')}
                action='ADD'
                handleClick={() => RestaurantManageData.openCreateModeDialog()}
            />
            <ArchiveCreateModeDialog
                open={RestaurantManageData.isCreateModeDialogOpen}
                entityType='restaurant'
                groupName={groupName}
                onClose={() => RestaurantManageData.closeCreateModeDialog()}
                onManualEntry={() => RestaurantManageData.continueManualCreate()}
                onScanFile={async (file) => {
                    const check = validateAnagraficaOcrFile(file);
                    if (check !== 'ok') {
                        return;
                    }
                    const result = await apiManage.parse_anagrafica_ocr(file, 'restaurant');
                    if (!result || result.error || !result.session_id) {
                        GlobalNotification.error(i18n.t('archive_form_ocr_parse_failed'));
                        return;
                    }
                    RestaurantManageData.openCreateWithOcr(result, file);
                }}
            />
            <RestaurantDialog RestaurantManageData={RestaurantManageData} />
        </>
    );
});

/**
 * 餐厅管理页面参数。
 */
type RestaurantManagerProps = {
    /** 当前集团名称。 */
    groupName: string;
};

/**
 * 展示当前集团下的餐厅管理页面。
 * @param props 当前集团上下文
 * @returns 餐厅管理页面
 */
export const RestaurantManager = observer(({
    groupName,
}: RestaurantManagerProps) => {
    useTranslation();

    const RestaurantManageData = useLocalObservable(() => new RestaurantManageState());

    // 搜索ref
    const SearchKeyInputRef = React.useRef<{ setSelectKey: (val: string) => void; }>();
    const getSortStatus = (key: string) => {
        const listSort = RestaurantManageData.listSort;
        if (key === listSort.sort_key) {
            return listSort.sort_control
        }
        return 0
    }
    // 表格表头包含数据列与保留的行操作列
    const headCellList: Array<HeadCell> = [
        ...createRestaurantHeadCellList(getSortStatus),
        {
            id: '5', label: '', sortStatus: -1, renderFunction(row) {
                return renderFeatures(row)
            },
        }
    ];

    const { list, isTableLoading, confirmDialogData, restaurantAuthDialog, conditions } = RestaurantManageData;

    React.useEffect(() => {
        RestaurantManageData.init();
    }, []);
    // 业务标签数组  静态数据
    const business_labels: { value: string; label: string; }[] = [
        { value: 'POS', label: 'POS' },
        { value: 'OD', label: 'OD' },
        { value: 'Site', label: 'Site' },
        { value: 'MKT', label: 'MKT' },
    ];
    // 下拉框 左边搜索条件
    const filterList: SearchKeyInputItems[] = [
        {
            value: 'nickname',
            label: i18n.t('restaurant_management_page_table_text_restaurant_name'),
            type: 'TEXT'
        },
        {
            value: 'tel',
            label: i18n.t('general_text_phone'),
            type: 'TEXT'
        }, {
            value: 'city',
            label: i18n.t('group_management_page_text_city'),
            type: 'TEXT'
        }, {
            value: 'string_id',
            label: i18n.t('restaurant_page_input_text_restaurant_identification_code'),
            type: 'TEXT'
        },
        {
            value: 'business_labels',
            label: i18n.t('restaurant_info_page_text_service_instance_tag'),
            type: 'SELECT',
            options: business_labels
        }
    ];



    // table里面的功能键
    const renderFeatures = (row: any): React.ReactNode => {
        return (
            <TableRowFeatures
                buttons={[
                    {
                        color: 'secondary',
                        action: 'DELETE',
                        handleClick: () => {
                            RestaurantManageData.updateConfirmDialogData({
                                visible: true,
                                forbidConfirm: 0,
                                title: i18n.t('general_text_tips'),
                                content: i18n.t('general_text_whether_to_delete_the_selection'),
                                handleClick: async () => {
                                    if (row._id) {
                                        await RestaurantManageData.handleDelete([row._id]);
                                    }
                                }
                            })
                        }
                    }, {
                        action: 'VIEW',
                        handleClick: () => {
                            if (row._id) {
                                RestaurantManageData.handleEdit(row._id);
                            }
                        }
                    }, {
                        titleText: i18n.t('warrant_page_text_authorization_scheme'),
                        children: (<CardMembership />),
                        handleClick: () => {
                            if (row._id) {
                                RestaurantManageData.updateRestaurantAuthDialog(true, row.string_id);
                            }
                        }
                    }
                ]} />
        )
    }
    return (
        <div className='restaurantManage'>
            {/* 搜索 */}
            <Box>
                <Box className='table-top-tool' >
                    <Box className={'left-tool'}>
                        {/* 新增按钮 */}
                        <RestaurantCreateEntry
                            RestaurantManageData={RestaurantManageData}
                            groupName={groupName}
                        />
                        <SearchKeyInput
                            ref={SearchKeyInputRef}
                            items={filterList}
                            conditions={toJS(conditions)}
                            onSearch={(conditions: ConditionType[]) => {
                                RestaurantManageData.setConditions(conditions);
                            }}
                        />
                    </Box>
                </Box>
                <Box>
                    <ConditionsBox conditions={toJS(conditions)} onDelete={(conditions) => {
                        RestaurantManageData.setConditions(conditions);
                        SearchKeyInputRef?.current?.setSelectKey?.('');
                    }} />
                </Box>
            </Box>
            {/* 列表 */}
            <TableComponent
                {...{
                    showChecked: 1,
                    list: list ?? [],
                    // 开启表格自定义页脚
                    isUseCustomPagination: true,
                    headCellList,
                    isTableLoading,
                    isPagination: true,
                    allCount: list.length,
                    handleDeleteAll: async (selected) => {
                        await RestaurantManageData.handleDelete(_.map(selected, '_id'))
                    },
                    // changePageCallback: ({ page, curRowsPerPage }) => {
                    //     RestaurantManageData.getRestaurantCondition(page + 1, curRowsPerPage);
                    // },
                    handleSort: (headCell) => {
                        RestaurantManageData.handleSort(headCell);
                    }
                }}
            />
            {/* 二次确认删除弹窗 */}
            <DeleteDialog
                {...{
                    open: confirmDialogData.visible,
                    onCancel: () => {
                        RestaurantManageData.updateConfirmDialogData({
                            visible: false,
                            title: '',
                            content: ''
                        })
                    },
                    onConfirm: async () => { await confirmDialogData?.handleClick?.(); },
                    tip: confirmDialogData.content,
                    title: confirmDialogData.title,
                }} />
            {/* 授权方案 */}
            <RestaurantAuthDialog open={restaurantAuthDialog.open} onCancel={restaurantAuthDialog.onCancel} restaurantStringId={RestaurantManageData.restaurantStringId || ''} />
        </div>
    )
})
